// @ts-strict-ignore
import { createSelector } from 'reselect';
import type { State } from '~/store';
import type { Action, PublicId } from 'flow/store/engine/types';
import generateMergeDiff, { generateFocusedActionDiff } from 'flow/service/changelog/formatters/mergeDropdownFormatter/mergeDiff';
import type { MergeDiffResult } from 'flow/service/changelog/formatters/mergeDropdownFormatter/types';
import { Operation } from 'flow/service/changelog/types';
import { diffThresholdMoves } from 'flow/service/changelog/formatters/mergeDropdownFormatter/diffThresholdMoves';
import {
  selectActions,
  selectFocusedAction,
  selectInitialFocusedAction,
  selectWorkflowData,
  selectWorkflowProps,
} from './general';
import {
  selectInitialActions,
  selectInitialWorkflowData,
  selectInitialWorkflowProps,
} from './history';
import { selectDiffMode } from './ui';

export const selectWorkflowDiff = createSelector(
  [
    selectWorkflowData,
    selectWorkflowProps,
    selectInitialWorkflowData,
    selectInitialWorkflowProps,
    selectDiffMode,
  ],
  (currentData, currentProps, initialData, initialProps, isInDiffMode): MergeDiffResult => {
    if (isInDiffMode) {
      return generateMergeDiff({
        A: {
          version: initialData,
          props: initialProps,
        },
        B: {
          version: currentData,
          props: currentProps,
        },
        isDiffOrLinkedSnippetUpgrade: true,
      });
    }
    // empty diff result when rendering baseNode outside of diff view (for performance)
    return {
      added: new Set(),
      modified: new Set(),
      removed: new Set(),
    };
  },
);

/**
 * Builds an ActionDiffField object (record operation paths and diffStatuses) in order to
 * color in the various fields in the NodeEditorDrawer when in diff-mode
 */
export const selectActionFieldDiff = createSelector(
  [selectFocusedAction, selectInitialFocusedAction],
  (currentFocusedAction, initialFocusedAction) => generateFocusedActionDiff({
    actionA: initialFocusedAction,
    actionB: currentFocusedAction,
  }),
);

export const selectMovedThresholds = createSelector(
  [
    selectInitialWorkflowData,
    selectInitialWorkflowProps,
    selectWorkflowData,
    selectWorkflowProps,
    selectWorkflowDiff,
  ], (
    initialWorkflowData,
    initialWorkflowProps,
    currentWorkflowData,
    currentWorkflowProps,
    workflowDiff,
  ): Set<PublicId> => diffThresholdMoves({
    A: {
      version: currentWorkflowData,
      props: currentWorkflowProps,
    },
    B: {
      version: initialWorkflowData,
      props: initialWorkflowProps,
    },
    modified: workflowDiff.modified,
  }),
);

// Groups the actions from the workflow diff based on diff status for rendering in the DiffModeMenu
export const selectGroupedActions = createSelector([
  selectInitialActions,
  selectActions,
  selectWorkflowDiff,
], (initialActions, currentActions, workflowDiff): Record<Operation, Action[]> => {
  const allActionIds = new Set([
    ...Object.keys(currentActions ?? {}),
    ...Object.keys(initialActions ?? {}),
  ]);

  // Removed actions only exist in initialActions. Added/modified actions are
  // resolved from currentActions first since that's the current state. Every id
  // comes from one of the two maps, so this always resolves to an action.
  const resolvedAction = (id: string): Action => currentActions?.[id] ?? initialActions?.[id];

  const result: Record<Operation, Action[]> = {
    [Operation.Add]: [...allActionIds]
      .filter(id => workflowDiff.added.has(id)).map(resolvedAction),
    [Operation.Modify]: [...allActionIds]
      .filter(id => workflowDiff.modified.has(id)).map(resolvedAction),
    [Operation.Remove]: [...allActionIds]
      .filter(id => workflowDiff.removed.has(id)).map(resolvedAction),
  };

  return result;
});

export const selectDiffedActions = createSelector([
  selectInitialActions,
  selectActions,
  selectWorkflowDiff,
], (initialActions, currentActions, workflowDiff): Partial<Record<PublicId, Operation>> => {
  const allActionIds = new Set([
    ...Object.keys(currentActions ?? {}),
    ...Object.keys(initialActions ?? {}),
  ]);

  const result: Partial<Record<PublicId, Operation>> = {};

  for (const id of allActionIds) {
    if (workflowDiff.added.has(id)) {
      result[id] = Operation.Add;
    }
    if (workflowDiff.modified.has(id)) {
      result[id] = Operation.Modify;
    }
    if (workflowDiff.removed.has(id)) {
      result[id] = Operation.Remove;
    }
  }

  return result;
});

export function selectDiffActionByPublicId(publicId: PublicId) {
  return function selector(state: State): Operation | null {
    const diffedActions = selectDiffedActions(state);
    return diffedActions[publicId] ?? null;
  };
}

export function selectFocusedActionForDiff(state: State) {
  const focusedAction = selectFocusedAction(state);
  if (focusedAction || !selectDiffMode(state)) {
    return focusedAction ?? null;
  }

  const { focusedNodeId } = state.workflowEditor.ui;
  return selectInitialActions(state)?.[focusedNodeId] ?? null;
}

/**
 * Whether the focused action's editor drawer should render as a split diff view.
 * True only in diff mode when the focused action was added, modified, or removed.
 */
export const selectShouldBeSplit = createSelector([
  selectDiffedActions,
  selectFocusedActionForDiff,
  selectDiffMode,
], (diffedActions, focusedAction, isInDiffMode) => {
  if (!isInDiffMode || !focusedAction) return false;

  const focusedActionDiffStatus = diffedActions?.[focusedAction.publicId];
  return [Operation.Add, Operation.Modify, Operation.Remove].includes(focusedActionDiffStatus);
});
